import React from 'react';

import { entriesToIdsOrNames } from '@neptune/compare-domain';
import { usePolling } from '@neptune/shared/common-business-logic';
import { useMemoizedValue } from '@neptune/shared/common-util';
import { useExperimentsModeContext } from '@neptune/shared/leaderboard-business-logic';
import { TableViewParams } from '@neptune/shared/leaderboard-domain';

import { resolveAutoCompareEntries } from './resolve-auto-compare-entries';

type LimitAndSearchParams = {
  projectIdentifier: string;
  tableParams?: TableViewParams;
  autoComparePool?: number;
};

export function useResolveAutoCompare(props: LimitAndSearchParams) {
  const [resolved, setResolved] = React.useState<boolean>(false);
  const { experimentsOnlyEnabled } = useExperimentsModeContext();
  const memoized = useMemoizedValue({ ...props, experimentsOnlyEnabled });

  const start = React.useCallback(() => setResolved(false), [setResolved]);
  const end = React.useCallback(() => setResolved(true), [setResolved]);

  const { result } = usePolling(memoized, {
    resolver,
    pollingInterval: 30_000,
    onFetchStarted: start,
    onFetchFailed: end,
    onFetchSucceed: end,
  });

  return {
    runIdentificationPairs: result,
    resolved,
  };
}

const resolver = async ({
  autoComparePool,
  projectIdentifier,
  tableParams,
  experimentsOnlyEnabled,
}: LimitAndSearchParams & { experimentsOnlyEnabled: boolean }) => {
  if (autoComparePool === undefined || !tableParams) {
    return;
  }

  const { runIds, experimentNames } = entriesToIdsOrNames(
    await resolveAutoCompareEntries(projectIdentifier, tableParams, autoComparePool),
    experimentsOnlyEnabled,
    Array.from,
  );

  return [experimentNames, runIds].map((arr) => arr.reverse().slice(0, autoComparePool));
};
